/**
 * "Add manual session" (D6): a compact per-statement rating grid using the
 * level's official scale — ACC/MCC 5 options incl. N/A, PCC Observed /
 * Not Observed — plus an optional comment and the two C1 qualifiers.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { Text, View } from 'react-native';

import { reviewDesk } from '@/design-system';
import { useTranslation } from '@/hooks/useTranslation';
import {
  C1_QUALIFIERS,
  getLevelForm,
  type IcfFormLevel,
  type StatementRatingValue,
} from '@/data/icfFormStatements';
import type { C1Qualifier, SessionModality, SofMentorRating } from '@/types/mentorEngagements';
import { ActionButton, Chip, SegmentedField, TextField, mentorStyles } from './shared';
import { WorkspaceModal } from './WorkspaceModal';
import { needsLevelAnalysisMessage } from './levelAnalysis';

export interface ManualSessionPayload {
  session_date: string;
  modality: SessionModality;
  duration_minutes: number;
  mentor_ratings: Record<string, SofMentorRating>;
  c1_q1: C1Qualifier | null;
  c1_q2: C1Qualifier | null;
}

interface ManualSessionModalProps {
  visible: boolean;
  level: IcfFormLevel;
  initialPayload?: Partial<ManualSessionPayload>;
  onClose: () => void;
  onSave: (payload: ManualSessionPayload) => Promise<void>;
}

const MODALITIES: SessionModality[] = ['in_person', 'virtual', 'recorded'];

export function ManualSessionModal({ visible, level, initialPayload, onClose, onSave }: ManualSessionModalProps) {
  const { t, language } = useTranslation();
  const form = useMemo(() => getLevelForm(level), [level]);
  const scaleOptions = form.scale.options.map((option) => ({
    value: option.value,
    label: language === 'tr' ? option.label_tr : option.label_en,
  }));
  const totalStatements = form.competencies.reduce((sum, competency) => sum + competency.statements.length, 0);

  const [sessionDate, setSessionDate] = useState(new Date().toISOString().slice(0, 10));
  const [modality, setModality] = useState<SessionModality>('virtual');
  const [duration, setDuration] = useState('60');
  const [ratings, setRatings] = useState<Record<string, StatementRatingValue>>({});
  const [comments, setComments] = useState<Record<string, string>>({});
  const [c1q1, setC1q1] = useState<C1Qualifier | null>(null);
  const [c1q2, setC1q2] = useState<C1Qualifier | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!visible) return;
    setSessionDate(initialPayload?.session_date || new Date().toISOString().slice(0, 10));
    setModality(initialPayload?.modality || 'virtual');
    setDuration(String(initialPayload?.duration_minutes ?? 60));
    const initialRatings: Record<string, StatementRatingValue> = {};
    const initialComments: Record<string, string> = {};
    Object.entries(initialPayload?.mentor_ratings || {}).forEach(([id, item]) => {
      const rating = item.rating_label ?? (level === 'pcc' && typeof item.observed === 'boolean'
        ? (item.observed ? 'observed' : 'not_observed') : null);
      if (rating) initialRatings[id] = rating;
      if (item.comment) initialComments[id] = item.comment;
    });
    setRatings(initialRatings);
    setComments(initialComments);
    setC1q1(initialPayload?.c1_q1 ?? null);
    setC1q2(initialPayload?.c1_q2 ?? null);
    setError(null);
  }, [visible, level, initialPayload]);

  const ratedCount = Object.keys(ratings).length;

  const submit = async () => {
    if (saving) return;
    const parsedDate = new Date(`${sessionDate}T00:00:00Z`);
    if (!/^\d{4}-\d{2}-\d{2}$/.test(sessionDate) || !Number.isFinite(parsedDate.getTime()) || parsedDate.toISOString().slice(0, 10) !== sessionDate) {
      setError(t('mentorEngagements.manual.invalidDate'));
      return;
    }
    if (!/^\d+$/.test(duration.trim()) || !Number.isSafeInteger(Number(duration)) || Number(duration) <= 0) {
      setError(t('mentorEngagements.manual.invalidDuration'));
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const mentorRatings: Record<string, SofMentorRating> = { ...initialPayload?.mentor_ratings };
      Object.entries(ratings).forEach(([statementId, rating]) => {
        mentorRatings[statementId] = level === 'pcc'
          ? { observed: rating === 'observed', rating_label: rating, comment: comments[statementId] || null }
          : { rating_label: rating, comment: comments[statementId] || null };
      });
      Object.entries(comments).forEach(([statementId, comment]) => {
        mentorRatings[statementId] = { ...mentorRatings[statementId], comment: comment || null };
      });
      await onSave({
        session_date: sessionDate,
        modality,
        duration_minutes: Number(duration),
        mentor_ratings: mentorRatings,
        c1_q1: c1q1,
        c1_q2: c1q2,
      });
      onClose();
    } catch (saveError) {
      setError(needsLevelAnalysisMessage(saveError, level, t) ?? t('mentorEngagements.manual.error'));
    } finally {
      setSaving(false);
    }
  };

  const c1Options = [
    { value: 'observed' as C1Qualifier, label: language === 'tr' ? 'Gözlemlendi' : 'Observed' },
    { value: 'not_observed' as C1Qualifier, label: language === 'tr' ? 'Gözlemlenmedi' : 'Not Observed' },
  ];

  return (
    <WorkspaceModal
      testID="manual_session_modal"
      visible={visible}
      busy={saving}
      title={t(initialPayload ? 'mentorEngagements.manual.editTitle' : 'mentorEngagements.manual.title')}
      subtitle={t('mentorEngagements.manual.subtitle')}
      onClose={onClose}
      maxWidth={860}
      footer={(
        <>
          <Chip label={t('mentorEngagements.manual.progress', { rated: ratedCount, total: totalStatements })} />
          <ActionButton label={t('mentorEngagements.manual.cancel')} onPress={onClose} disabled={saving} />
          <ActionButton
            testID="manual_session_save"
            label={t('mentorEngagements.manual.save')}
            onPress={submit}
            variant="primary"
            disabled={saving}
          />
        </>
      )}
    >
      <View style={mentorStyles.row}>
        <TextField
          editable={!saving}
          label={t('mentorEngagements.manual.sessionDate')}
          value={sessionDate}
          onChangeText={setSessionDate}
          placeholder="YYYY-MM-DD"
        />
        <TextField
          editable={!saving}
          label={t('mentorEngagements.manual.duration')}
          value={duration}
          onChangeText={setDuration}
          keyboardType="numeric"
        />
        <SegmentedField<SessionModality>
          label={t('mentorEngagements.manual.modality')}
          options={MODALITIES.map((value) => ({ value, label: t(`mentorEngagements.modality.${value}`) }))}
          value={modality}
          disabled={saving}
          onChange={setModality}
        />
      </View>

      <View style={{ gap: 8, borderTopWidth: 1, borderTopColor: reviewDesk.line, paddingTop: 12 }}>
        <Text style={[mentorStyles.panelTitle, { fontSize: 15 }]}>{t('mentorEngagements.manual.c1Title')}</Text>
        <Text style={mentorStyles.mutedText}>{t('mentorEngagements.manual.c1Note')}</Text>
        {C1_QUALIFIERS.map((qualifier) => (
          <View key={qualifier.key} style={{ gap: 6 }}>
            <Text style={mentorStyles.bodyText}>
              {language === 'tr' ? qualifier.text_tr : qualifier.text_en}
            </Text>
            <SegmentedField<C1Qualifier>
              testID={`manual_session_c1_${qualifier.key}`}
              options={c1Options}
              value={qualifier.key === 'q1' ? c1q1 : c1q2}
              disabled={saving}
              onChange={qualifier.key === 'q1' ? setC1q1 : setC1q2}
            />
          </View>
        ))}
      </View>

      {form.competencies.map((competency) => (
        <View
          key={competency.code}
          style={{ gap: 12, borderTopWidth: 1, borderTopColor: reviewDesk.line, paddingTop: 12 }}
        >
          <Text style={[mentorStyles.panelTitle, { fontSize: 15 }]}>
            {competency.code} · {language === 'tr' ? competency.title_tr : competency.title_en}
          </Text>
          {competency.statements.map((statement) => (
            <View key={statement.id} style={{ gap: 6 }}>
              <Text style={mentorStyles.bodyText}>
                <Text style={{ fontWeight: '800', color: reviewDesk.ink }}>{statement.id}  </Text>
                {language === 'tr' ? statement.text_tr : statement.text_en}
              </Text>
              {(language === 'tr' ? statement.note_tr : statement.note_en) ? (
                <Text style={mentorStyles.mutedText}>
                  {language === 'tr' ? statement.note_tr : statement.note_en}
                </Text>
              ) : null}
              <SegmentedField
                testID={`manual_session_rating_${statement.id}`}
                options={scaleOptions}
                value={ratings[statement.id] ?? null}
                disabled={saving}
                onChange={(value) => setRatings((current) => ({ ...current, [statement.id]: value }))}
              />
              <TextField
                editable={!saving}
                label={t('mentorEngagements.manual.comment')}
                value={comments[statement.id] || ''}
                onChangeText={(value) => setComments((current) => ({ ...current, [statement.id]: value }))}
                placeholder={t('mentorEngagements.manual.commentPlaceholder')}
              />
            </View>
          ))}
        </View>
      ))}

      {error ? <Text testID="manual_session_error" style={mentorStyles.errorText}>{error}</Text> : null}
    </WorkspaceModal>
  );
}

export default ManualSessionModal;
